/**
 * SCH Summer Shuttles — School Access Transportation Report
 *
 * Public-facing page for SCH staff and families.
 * Shows live route status, weekly availability, and interest capture.
 * Data source: Firestore campRosters/summer-2026
 */

import React, { useState, useEffect } from 'react';
import { db } from '@/lib/firebase';
import { doc, getDoc, addDoc, collection, serverTimestamp } from 'firebase/firestore';
import type { ParsedRider } from '@/pages/admin/WishIntelligence';
import { CheckCircle, Clock, MapPin, Users, Truck, ChevronDown, ChevronUp, AlertCircle } from 'lucide-react';

// ─── Constants ────────────────────────────────────────────────────────────────

const WEEKS = [1,2,3,4,5,6,7,8,9,10];

const LOOPS = {
  early: {
    name: 'Early Loop',
    rate: '$165/week',
    rateNum: 165,
    pickupWindow: '7:15 – 7:45 AM',
    arrival: 'Arrive SCH ~8:15 AM',
    stops: ['Passyunk Square', 'Graduate Hospital', 'Washington Square', 'Fairmount'],
    stopTimes: ['~7:15 AM', '~7:25 AM', '~7:35 AM', '~7:45 AM'],
    color: 'blue',
    description: 'Serves South Philadelphia and Center City neighborhoods. Heads north toward Chestnut Hill.',
  },
  late: {
    name: 'Late Loop',
    rate: '$75/week',
    rateNum: 75,
    pickupWindow: '8:00 – 8:20 AM',
    arrival: 'Arrive SCH ~8:35 AM',
    stops: ['Manayunk/Roxborough', 'Chestnut Hill'],
    stopTimes: ['~8:00 AM', '~8:20 AM'],
    color: 'green',
    description: 'Serves the Northwest corridor. Discounted rate for proximity to SCH.',
  },
} as const;

type LoopKey = keyof typeof LOOPS;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function studentsOnLoopWeek(loop: LoopKey, week: number, riders: ParsedRider[]) {
  return riders
    .filter(r => r.loop === loop && r.weeks.includes(week))
    .reduce((sum, r) => sum + r.students, 0);
}

function loopHasAnyRiders(loop: LoopKey, riders: ParsedRider[]) {
  return riders.some(r => r.loop === loop && r.weeks.length > 0);
}

// ─── Interest Form ────────────────────────────────────────────────────────────

interface InterestForm {
  name: string;
  email: string;
  phone: string;
  pickup: string;
  students: string;
  weeks: string;
  preferredTime: string;
  notes: string;
}

const BLANK_FORM: InterestForm = {
  name: '', email: '', phone: '', pickup: '', students: '1', weeks: '', preferredTime: '', notes: '',
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function WeekBadge({ week, hasStudents, count }: { week: number; hasStudents: boolean; count: number }) {
  return (
    <div className={`flex flex-col items-center px-3 py-2 rounded-lg border text-center min-w-[52px] ${
      hasStudents ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-200'
    }`}>
      <span className={`text-xs font-semibold ${hasStudents ? 'text-green-700' : 'text-gray-400'}`}>Wk {week}</span>
      <span className={`text-lg font-bold leading-none mt-0.5 ${hasStudents ? 'text-green-900' : 'text-gray-300'}`}>
        {count || '—'}
      </span>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function SCHSummerReport() {
  const [bookedRiders, setBookedRiders] = useState<ParsedRider[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedLoop, setExpandedLoop] = useState<LoopKey | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<InterestForm>(BLANK_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDoc(doc(db, 'campRosters', 'summer-2026'));
        if (snap.exists()) {
          setBookedRiders(snap.data().bookedRiders ?? []);
        }
      } catch (e) {
        console.error('Error loading camp roster:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    if (!form.name || !form.email || !form.pickup) {
      setFormError('Please fill in name, email, and pickup neighborhood.');
      return;
    }
    setSubmitting(true);
    try {
      await addDoc(collection(db, 'campInterest'), {
        ...form,
        students: parseInt(form.students) || 1,
        source: 'sch-summer-report',
        createdAt: serverTimestamp(),
      });
      setSubmitted(true);
      setForm(BLANK_FORM);
    } catch (err) {
      setFormError('Something went wrong. Please try again.');
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const totalBookedStudents = bookedRiders.reduce((s, r) => s + r.students, 0);
  const weeksWithService = WEEKS.filter(w =>
    studentsOnLoopWeek('early', w, bookedRiders) + studentsOnLoopWeek('late', w, bookedRiders) > 0
  ).length;

  return (
    <div className="min-h-screen bg-white">

      {/* Hero */}
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-5xl mx-auto px-4 py-12 sm:py-16">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-shuttlebee-yellow rounded-lg flex items-center justify-center flex-shrink-0">
              <Truck className="w-6 h-6 text-gray-900" />
            </div>
            <div>
              <div className="text-xs font-semibold text-yellow-400 uppercase tracking-widest">Shuttlebee × SCH</div>
              <h1 className="text-2xl sm:text-3xl font-bold leading-tight">Summer Camp Transportation</h1>
            </div>
          </div>
          <p className="text-gray-300 text-base sm:text-lg max-w-2xl mb-6">
            Weekly door-to-camp shuttle service for Summerside Camps at SCH, 8000 Cherokee St, Philadelphia.
            Pick the weeks you need — no full-season commitment required.
          </p>
          <div className="flex flex-wrap gap-4 text-sm">
            <div className="flex items-center gap-2 bg-white/10 rounded-full px-3 py-1.5">
              <CheckCircle className="w-4 h-4 text-yellow-400" />
              Book week by week
            </div>
            <div className="flex items-center gap-2 bg-white/10 rounded-full px-3 py-1.5">
              <MapPin className="w-4 h-4 text-yellow-400" />
              Neighborhood pickup stops
            </div>
            <div className="flex items-center gap-2 bg-white/10 rounded-full px-3 py-1.5">
              <Users className="w-4 h-4 text-yellow-400" />
              Small group shuttles
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-10">

        {/* Live stats */}
        {!loading && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-10">
            {[
              { label: 'Families Enrolled', value: bookedRiders.length, color: 'text-green-700' },
              { label: 'Students Riding', value: totalBookedStudents, color: 'text-green-700' },
              { label: 'Active Routes', value: Object.keys(LOOPS).filter(l => loopHasAnyRiders(l as LoopKey, bookedRiders)).length, color: 'text-blue-700' },
              { label: 'Weeks of Service', value: weeksWithService, color: 'text-gray-700' },
            ].map(s => (
              <div key={s.label} className="border border-gray-200 rounded-xl p-4 text-center">
                <div className={`text-3xl font-bold ${s.color}`}>{s.value}</div>
                <div className="text-xs text-gray-500 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Route Cards */}
        <h2 className="text-xl font-bold text-gray-900 mb-4">Active Shuttle Routes</h2>
        <div className="space-y-4 mb-10">
          {(Object.entries(LOOPS) as [LoopKey, typeof LOOPS[LoopKey]][]).map(([key, loop]) => {
            const isExpanded = expandedLoop === key;
            const colorMap = {
              blue: { header: 'bg-blue-600', badge: 'bg-blue-100 text-blue-800', border: 'border-blue-200', dot: 'bg-blue-500' },
              green: { header: 'bg-green-600', badge: 'bg-green-100 text-green-800', border: 'border-green-200', dot: 'bg-green-500' },
            };
            const c = colorMap[loop.color];

            return (
              <div key={key} className={`border ${c.border} rounded-xl overflow-hidden`}>
                {/* Header */}
                <div
                  className={`${c.header} text-white px-5 py-4 flex items-start justify-between cursor-pointer`}
                  onClick={() => setExpandedLoop(isExpanded ? null : key)}
                >
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="text-lg font-bold">{loop.name}</h3>
                      <span className="text-sm font-semibold bg-white/20 px-2 py-0.5 rounded-full">{loop.rate}</span>
                    </div>
                    <div className="text-sm text-white/80 flex flex-wrap gap-4">
                      <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Pickup {loop.pickupWindow}</span>
                      <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {loop.arrival}</span>
                    </div>
                  </div>
                  {isExpanded ? <ChevronUp className="w-5 h-5 mt-1 flex-shrink-0" /> : <ChevronDown className="w-5 h-5 mt-1 flex-shrink-0" />}
                </div>

                {/* Always-visible: weekly enrollment grid */}
                <div className="bg-white px-5 py-4">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Students enrolled by week</div>
                  <div className="flex flex-wrap gap-2">
                    {WEEKS.map(w => {
                      const count = studentsOnLoopWeek(key, w, bookedRiders);
                      return <WeekBadge key={w} week={w} hasStudents={count > 0} count={count} />;
                    })}
                  </div>
                </div>

                {/* Expanded: stops + description */}
                {isExpanded && (
                  <div className="bg-gray-50 border-t border-gray-200 px-5 py-4">
                    <p className="text-sm text-gray-600 mb-4">{loop.description}</p>
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Morning pickup stops</div>
                    <div className="space-y-2">
                      {loop.stops.map((stop, i) => {
                        const hasRiders = bookedRiders.some(r =>
                          r.loop === key && r.pickup.toLowerCase().includes(stop.toLowerCase().split('/')[0])
                        );
                        return (
                          <div key={stop} className="flex items-center gap-3">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0 ${hasRiders ? c.dot : 'bg-gray-300'}`}>
                              {i + 1}
                            </div>
                            <div className="flex-1 text-sm">
                              <span className={hasRiders ? 'text-gray-900 font-medium' : 'text-gray-400'}>{stop}</span>
                              <span className="text-gray-400 ml-2 text-xs">{loop.stopTimes[i]}</span>
                            </div>
                            {hasRiders && (
                              <span className={`text-xs px-2 py-0.5 rounded-full ${c.badge}`}>Active</span>
                            )}
                          </div>
                        );
                      })}
                      <div className="flex items-center gap-3 mt-2">
                        <div className="w-6 h-6 rounded-full bg-shuttlebee-yellow flex items-center justify-center flex-shrink-0">
                          <MapPin className="w-3.5 h-3.5 text-gray-900" />
                        </div>
                        <span className="text-sm font-semibold text-gray-700">SCH · 8000 Cherokee St, Philadelphia</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Weekly Snapshot */}
        <h2 className="text-xl font-bold text-gray-900 mb-4">Weekly Enrollment Snapshot</h2>
        <div className="border border-gray-200 rounded-xl overflow-hidden mb-10">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 font-semibold text-gray-700">Week</th>
                <th className="text-center px-3 py-3 font-semibold text-blue-700">Early Loop</th>
                <th className="text-center px-3 py-3 font-semibold text-green-700">Late Loop</th>
                <th className="text-center px-3 py-3 font-semibold text-gray-700">Total Students</th>
                <th className="text-center px-3 py-3 font-semibold text-gray-500">Seats Still Open*</th>
              </tr>
            </thead>
            <tbody>
              {WEEKS.map(week => {
                const early = studentsOnLoopWeek('early', week, bookedRiders);
                const late = studentsOnLoopWeek('late', week, bookedRiders);
                const total = early + late;
                const openEarly = Math.max(0, 14 - early);
                const openLate = Math.max(0, 14 - late);
                const openTotal = openEarly + openLate;
                return (
                  <tr key={week} className={`border-b border-gray-100 ${total === 0 ? 'opacity-50' : ''}`}>
                    <td className="px-4 py-2.5 font-medium text-gray-900">Week {week}</td>
                    <td className="px-3 py-2.5 text-center">
                      {early > 0 ? <span className="inline-block bg-blue-100 text-blue-800 font-semibold px-2 py-0.5 rounded text-xs">{early}</span> : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      {late > 0 ? <span className="inline-block bg-green-100 text-green-800 font-semibold px-2 py-0.5 rounded text-xs">{late}</span> : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-3 py-2.5 text-center font-semibold text-gray-900">
                      {total > 0 ? total : <span className="text-gray-300">0</span>}
                    </td>
                    <td className="px-3 py-2.5 text-center text-gray-500 text-xs">
                      {total > 0 ? `~${openTotal} seats` : 'Open'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 text-xs text-gray-400">
            * Estimated based on 14-seat capacity per loop. New routes may open as interest grows.
          </div>
        </div>

        {/* How It Works */}
        <h2 className="text-xl font-bold text-gray-900 mb-4">How It Works</h2>
        <div className="grid sm:grid-cols-3 gap-4 mb-10">
          {[
            { step: '1', title: 'Choose Your Weeks', body: 'Select any combination of the 10 camp weeks. No season-long commitment — pay only for the weeks you need.' },
            { step: '2', title: 'Pick Your Stop', body: 'We pick up from neighborhood hubs. Your child boards near home and arrives at SCH ready for camp.' },
            { step: '3', title: 'We Handle the Rest', body: 'Small group shuttles, vetted Shuttlebee drivers, and real-time updates throughout the summer.' },
          ].map(item => (
            <div key={item.step} className="border border-gray-200 rounded-xl p-5">
              <div className="w-8 h-8 bg-shuttlebee-yellow rounded-full flex items-center justify-center text-sm font-bold text-gray-900 mb-3">{item.step}</div>
              <h3 className="font-semibold text-gray-900 mb-1">{item.title}</h3>
              <p className="text-sm text-gray-600">{item.body}</p>
            </div>
          ))}
        </div>

        {/* Interest / Join Form */}
        <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6 sm:p-8">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-10 h-10 bg-shuttlebee-yellow rounded-lg flex items-center justify-center flex-shrink-0">
              <Users className="w-5 h-5 text-gray-900" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Add Your Family to a Route</h2>
              <p className="text-gray-600 text-sm mt-1">
                New families are added to routes on a rolling basis throughout the summer. Tell us which weeks and stops work for you.
              </p>
            </div>
          </div>

          {submitted ? (
            <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl p-5">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
              <div>
                <div className="font-semibold text-green-900">You're on the list!</div>
                <div className="text-sm text-green-700">We'll reach out with availability and pricing for your selected weeks. You can submit again for additional children or preferences.</div>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Parent / Guardian Name *</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-shuttlebee-yellow focus:border-transparent"
                    placeholder="Jane Smith"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-shuttlebee-yellow focus:border-transparent"
                    placeholder="jane@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-shuttlebee-yellow focus:border-transparent"
                    placeholder="(215) 555-0100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Number of Children</label>
                  <select
                    value={form.students}
                    onChange={e => setForm(f => ({ ...f, students: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-shuttlebee-yellow focus:border-transparent"
                  >
                    {[1,2,3,4].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pickup Neighborhood / Hub *</label>
                  <select
                    value={form.pickup}
                    onChange={e => setForm(f => ({ ...f, pickup: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-shuttlebee-yellow focus:border-transparent"
                  >
                    <option value="">Select a stop…</option>
                    <optgroup label="Early Loop ($165/week)">
                      <option>Passyunk Square</option>
                      <option>Graduate Hospital</option>
                      <option>Washington Square</option>
                      <option>Fairmount</option>
                    </optgroup>
                    <optgroup label="Late Loop ($75/week)">
                      <option>Manayunk/Roxborough</option>
                      <option>Chestnut Hill</option>
                    </optgroup>
                    <option value="Other / Not Listed">Other / Not Listed</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Pickup Time</label>
                  <select
                    value={form.preferredTime}
                    onChange={e => setForm(f => ({ ...f, preferredTime: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-shuttlebee-yellow focus:border-transparent"
                  >
                    <option value="">No preference</option>
                    <option>7:15 – 7:30 AM</option>
                    <option>7:30 – 7:45 AM</option>
                    <option>8:00 – 8:15 AM</option>
                    <option>8:15 – 8:30 AM</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Which weeks do you need? (e.g. 1, 2, 5–8)</label>
                <input
                  type="text"
                  value={form.weeks}
                  onChange={e => setForm(f => ({ ...f, weeks: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-shuttlebee-yellow focus:border-transparent"
                  placeholder="e.g. 1, 3, 4, 7, 8 — or All"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Anything else we should know?</label>
                <textarea
                  value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-shuttlebee-yellow focus:border-transparent resize-none"
                  placeholder="Sibling drop-offs, timing constraints, questions…"
                />
              </div>
              {formError && (
                <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg p-3">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {formError}
                </div>
              )}
              <button
                type="submit"
                disabled={submitting}
                className="w-full sm:w-auto px-6 py-2.5 bg-shuttlebee-yellow text-gray-900 font-semibold rounded-lg hover:bg-yellow-400 transition-colors disabled:opacity-50 text-sm"
              >
                {submitting ? 'Submitting…' : 'Add Me to a Route →'}
              </button>
            </form>
          )}
        </div>

        {/* Footer note */}
        <div className="mt-8 text-center text-xs text-gray-400">
          Questions? Contact Shuttlebee at{' '}
          <a href="mailto:hello@getshuttlebee.com" className="underline hover:text-gray-600">hello@getshuttlebee.com</a>
          {' '}· Powered by Shuttlebee
        </div>
      </div>
    </div>
  );
}
